import React, { useRef, useState } from "react";
import { useTranslation } from "shared/i18n/useTranslation";
import { useToast } from "shared/toast/useToast";
import { uploadFileToS3 } from "shared/upload/uploadFileToS3";
import { getDomainName } from "shared/url/getDomainName";
import { useIsMounted } from "shared/useIsMounted";

const reducer = require("image-blob-reduce")();

interface UploadImageButtonType {
  labelClassName?: string;
  label?: string;
  fileInput?: React.InputHTMLAttributes<HTMLInputElement>;
  onFileUpload: (filePath: string) => void;
}

const urlPrefix = `https://content.${getDomainName()}`;
export function UploadImageButton({
  fileInput,
  label,
  labelClassName = "",
  onFileUpload,
}: UploadImageButtonType) {
  // File upload might take a while and parent component might have passed different callbacks during rerender
  // Keeping it in ref and using it when we are ready helps with this issue by always calling the latest callback
  const onFileUploadRef = useRef(onFileUpload);
  onFileUploadRef.current = onFileUpload;
  const { isMounted } = useIsMounted();
  const { translate } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadFailed, setIsUploadFailed] = useState(false);
  const { error } = useToast();

  async function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files?.length) {
      return;
    }
    const file = e.target.files[0];
    const resetSelectedFile = () => (e.target.value = "");
    try {
      setIsUploading(true);
      setIsUploadFailed(false);
      const blob = await reducer.toBlob(file, {
        max: 800,
        unsharpAmount: 80,
        unsharpRadius: 0.6,
        unsharpThreshold: 2,
      });

      const smallFile = new File([blob], "SmallFile.jpeg");
      const { key } = await uploadFileToS3(
        smallFile,
        "us-east-2:ce2bb674-112e-4613-bcc2-22fe490f64be",
        "us-east-2",
        "content.evergreenairsolutions.com",
        "TemporaryUploads/",
        true
      );
      const url = `${urlPrefix}/${key}`;
      onFileUploadRef.current(url);
      setIsUploading(false);
      resetSelectedFile();
    } catch (err) {
      if (isMounted()) {
        resetSelectedFile();
        setIsUploadFailed(true);
        setIsUploading(false);
        error(translate("__uploadingFailedTryAgain"));
      }
    }
  }

  return (
    <label
      className={`btn ${
        isUploadFailed ? "btn-outline-danger" : "btn-outline-primary"
      } ${isUploading ? "disabled" : ""} ${labelClassName}`}
    >
      {isUploading
        ? translate("__uploading")
        : label ?? translate("__uploadPhoto")}
      <input
        type="file"
        disabled={isUploading}
        hidden
        {...presets.imageCapture}
        {...fileInput}
        onChange={handleChange}
      />
    </label>
  );
}

interface PresetsType {
  imageCaptureOfEnvironment: React.InputHTMLAttributes<HTMLInputElement>;
  imageCaptureSelfie: React.InputHTMLAttributes<HTMLInputElement>;
  imageCapture: React.InputHTMLAttributes<HTMLInputElement>;
}
export const presets: PresetsType = {
  imageCapture: {
    accept: "image/*",
  },
  imageCaptureOfEnvironment: {
    accept: "image/*",
    capture: "environment",
  },
  imageCaptureSelfie: {
    accept: "image/*",
    capture: "user",
  },
};
